import { createDataItemSigner, monitor } from "@permaweb/aoconnect"

import { AoInstance, Message } from "./ao"
import { getLinkedMessages } from "./messages-api"
import { Reserves } from "./pool"
import { transfer } from "./token"
import { StopLosOrderFormValues } from "../components/swap/types/StopLossOrderForm"
import { AGENTS_FACTORY, LONG_OPERATION, NORMAL_OPERATION } from "../settings"
import { flattenTags } from "../utils/arweave"
import { parseNumberAsBigInt } from "../utils/format"
import { retryable } from "../utils/utils"

export type OrderType = "dca-order" | "limit-order" | "stop-order" | "hopper-order"

export interface OrderAgent {
  amm_process: string
  type: OrderType
  min_output?: string
  token_to: string
  id: string
  input?: string
  expires_at_ts?: number
  order_owner: string
  token_from: string
  created_at_ts?: number
}

export async function getActiveOrders(activeAddr: string, ao: AoInstance): Promise<OrderAgent[]> {
  const res = await ao.dryrun({
    process: AGENTS_FACTORY,
    Timestamp: 0,
    tags: [
      { name: "Action", value: "Get-Agents" },
      // { name: "Agent-Type", value: "limit-order" },
      { name: "Order-Owner", value: activeAddr },
      { name: "Progress-Filter", value: "pending" },
    ],
  })

  for (const msg of res.Messages as Message[]) {
    const val = JSON.parse(msg.Data)

    if (val) return val as OrderAgent[]
  }

  throw new Error("Response malformed")
}

async function readAgentId(requestId: string): Promise<string> {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, requestId)

  const process = messages.find((msg) => msg.tags["Type"] === "Process")
  if (!process) throw new Error("Agent Id not found")

  console.log("onAgentIdFromGateway", process.id)
  return process.id
}

async function readAgentIdFromCU(requestId: string, ao: AoInstance): Promise<string> {
  try {
    const res = await ao.dryrun({
      process: AGENTS_FACTORY,
      tags: [
        { name: "Action", value: "Get-Agent-Id" },
        { name: "Request-Id", value: requestId },
      ],
    })

    const agentIdResp = JSON.parse(res.Messages[0].Data) as {
      ["Request-Id"]: string
      ["Agent-Id"]: string
    }

    if (!agentIdResp["Agent-Id"]) throw new Error("Agent Id (from CU) not found")

    console.log("onAgentIdFromCU", agentIdResp["Agent-Id"])

    return agentIdResp["Agent-Id"]
  } catch (e) {
    console.error(e)
    throw new Error("Agent Id (from CU) not found")
  }
}

type AgentStatus =
  | "AwaitingStrategyConfiguration"
  | "AwaitingDeposit"
  | "Swapping"
  | "Complete"
  | "Expired"
  | "Ready"
  | "Canceled"
  | "AwaitingSubscription"

type Primitives = {
  "stop-limit-order"?: {
    "Filled-Price": string
    "Available-Input": string
    "Accumulated-Output": string
    "Token-To": string
    "Price-Precision": string
    "Is-Complete": boolean
    "Min-Output": string
    "Is-Initialized": boolean
    "Token-From": string
    Swaps: any[]
    Price: string
    "Filled-Percentage": string
  }
  "main-limit-order"?: {
    "Filled-Price": string
    "Available-Input": string
    "Accumulated-Output": string
    "Token-To": string
    "Price-Precision": string
    "Is-Complete": string
    "Min-Output": string
    "Is-Initialized": string
    "Token-From": string
    Swaps: any[]
    Price: string
    "Filled-Percentage": string
  }
  "stop-trigger": {
    "Is-Trailing": string //"false",
    "Last-Price-Received": string //"4159514090998682664"
    "Price-Precision": string //"18"
    "Token-To": string //"0udHxHUaSZI4aIs4hD6rF2jRas4G_XWYnn6JwxXd0II"
    "Is-Initialized": string //"true"
    "Token-From": string //"uc6uA_oCgDRNCSyweh1K8QBg4DqXBadZPBBxX82PxiQ"
    "Stop-Price": string //"4"
    "Was-Hit": string // "false"
    "Trigger-Direction": string //"Stop"
  }
}

type Strategy = {
  "Is-Trailing"?: "false" | "true"
  "Is-Stop-Limit"?: "false" | "true"
  "Has-Stop-Loss": "false" | "true"
  "Stop-Limit-Min-Output"?: string
  "Trailing-Difference"?: string
  Primitives?: Primitives
  "Min-Output": string
  // DCA order
  "Filled-Price"?: string
  "Filled-Percentage"?: string
  "Accumulated-Output"?: string
  "Stop-Price"?: string
}

export interface AgentInfo {
  "Agent-Type": "limit-order" | "dca-order"
  Status: AgentStatus
  "Has-Pending-Cancellation": "false" | "true"
  Strategy: Strategy
  "Token-From": string
  "Token-To": string
  Input: string
  "Order-Owner": string
  "Expires-At": string
}

export async function readAgentInfo(agentId: string, ao: AoInstance): Promise<AgentInfo> {
  try {
    const res = await ao.dryrun({
      process: agentId,
      Timestamp: 0,
      tags: [{ name: "Action", value: "Info" }],
    })

    const agentInfo = JSON.parse(res.Messages[0].Data) as AgentInfo
    return agentInfo
  } catch (e) {
    console.error(e)
    throw new Error("Agent Info not found")
  }
}

async function readAgentStatus(agentId: string, ao: AoInstance): Promise<AgentStatus> {
  const info = await readAgentInfo(agentId, ao)
  return info.Status
}

async function waitForAgentAwaitingDeposit(agentId: string, ao: AoInstance): Promise<AgentStatus> {
  const info = await readAgentInfo(agentId, ao)
  info.Status as AgentStatus

  if (info.Status === "AwaitingDeposit") return info.Status

  throw new Error("Agent not ready")
}

export interface LimitOrderParams {
  input: bigint
  output: bigint
  fromTokenId: string
  toTokenId: string
  pool: string
  expiry?: number
}

export async function limitOrder(
  data: LimitOrderParams,
  ao: AoInstance,
  onPrepared?: () => void,
): Promise<string> {
  const requestId = await ao.message({
    process: AGENTS_FACTORY,
    signer: createDataItemSigner(window.arweaveWallet),
    tags: [
      { name: "Action", value: "Create-Limit-Order" },
      { name: "Amm", value: data.pool },
      { name: "Token-From", value: data.fromTokenId },
      { name: "Token-To", value: data.toTokenId },
      { name: "Input", value: data.input.toString() },
      { name: "Min-Output", value: data.output.toString() },
      { name: "Expires-At", value: String(Date.now() + 1000 * 60 * (data.expiry || 3 * 24 * 60)) },
    ],
  })
  console.log("onRequestId", requestId)

  const computedResult = await ao.result({
    message: requestId,
    process: AGENTS_FACTORY,
  })

  console.log("onRequestComputed", computedResult)

  if (
    computedResult.Messages.length === 0 &&
    computedResult.Spawns.length === 0 &&
    computedResult.Output.data
  ) {
    throw new Error(computedResult.Output.data)
  }

  if (
    computedResult.Messages.length === 0 &&
    computedResult.Spawns.length === 0 &&
    computedResult.Error
  ) {
    throw new Error(computedResult.Error)
  }

  const agentId = await Promise.race([
    retryable(readAgentId, LONG_OPERATION)(requestId),
    retryable(readAgentIdFromCU, LONG_OPERATION)(requestId, ao),
  ])
  console.log("onAgentId", agentId)

  const status = await retryable(readAgentStatus)(agentId, ao)
  console.log("onAgentStatus", status)

  if (status !== "AwaitingDeposit" && status !== "AwaitingStrategyConfiguration")
    throw new Error("Unexpected Agent status")

  onPrepared?.()

  // Ensure that we are ready to deposit
  await retryable(waitForAgentAwaitingDeposit)(agentId, ao)

  const transferId = await transfer(
    {
      token: data.fromTokenId,
      recipient: agentId,
      quantity: data.input,
    },
    createDataItemSigner(window.arweaveWallet),
    ao,
  )
  console.log("onTransferId", transferId)

  return agentId
}

async function readCancelConfirmation(cancelId: string): Promise<string> {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, cancelId)

  const cancelConfirmation = messages.find(
    (msg) => msg.tags["Action"] === "Event-Finalized-Botega-Agent",
  )
  if (!cancelConfirmation) throw new Error("Cancel Confirmation not found")

  return cancelConfirmation.id
}

export async function cancelLimitOrder(agentId: string, ao: AoInstance): Promise<void> {
  const cancelId = await ao.message({
    process: agentId,
    signer: createDataItemSigner(window.arweaveWallet),
    tags: [{ name: "Action", value: "Cancel-Order" }],
  })
  console.log("onCancelId", cancelId)

  const cancelConfirmationId = await retryable(readCancelConfirmation, NORMAL_OPERATION)(cancelId)
  console.log("onCancelConfirmationId", cancelConfirmationId)
}

async function readHopperCancelConfirmation(cancelId: string): Promise<string> {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, cancelId)

  const cancelConfirmation = messages.find((msg) => msg.tags["Action"] === "Transfer")
  if (!cancelConfirmation) throw new Error("Cancel Confirmation not found")

  return cancelConfirmation.id
}

export async function cancelHopperOrder(agentId: string, ao: AoInstance): Promise<void> {
  const cancelId = await ao.message({
    process: agentId,
    signer: createDataItemSigner(window.arweaveWallet),
    tags: [{ name: "Action", value: "Emergency-Refund" }],
  })
  console.log("onCancelId", cancelId)

  const cancelConfirmationId = await retryable(
    readHopperCancelConfirmation,
    NORMAL_OPERATION,
  )(cancelId)
  console.log("onCancelConfirmationId", cancelConfirmationId)
}

async function confirmTopUp(agentId: string, ao: AoInstance): Promise<AgentStatus> {
  const info = await readAgentInfo(agentId, ao)

  if (info.Status === "AwaitingDeposit") throw new Error("Top-up not confirmed")

  return info.Status
}

export async function topUpLimitOrder(
  data: Pick<LimitOrderParams, "input" | "fromTokenId">,
  agentId: string,
  ao: AoInstance,
): Promise<string> {
  const transferId = await transfer(
    {
      token: data.fromTokenId,
      recipient: agentId,
      quantity: data.input,
    },
    createDataItemSigner(window.arweaveWallet),
    ao,
  )
  console.log("onTransferId", transferId)

  const status = await retryable(confirmTopUp)(agentId, ao)
  console.log("onAgentStatus", status)

  return agentId
}

export async function topUpDcaOrder(
  data: Pick<DcaOrderParams, "input" | "fromTokenId">,
  agentId: string,
  ao: AoInstance,
): Promise<string> {
  const monitorId = await retryable(monitorAgent)(agentId, ao)
  console.log("onMonitorId", monitorId)

  const transferId = await transfer(
    {
      token: data.fromTokenId,
      recipient: agentId,
      quantity: data.input,
    },
    createDataItemSigner(window.arweaveWallet),
    ao,
  )
  console.log("onTransferId", transferId)

  const status = await retryable(confirmTopUp)(agentId, ao)
  console.log("onAgentStatus", status)

  return agentId
}

export interface DcaOrderParams {
  input: bigint
  output: bigint
  fromTokenId: string
  toTokenId: string
  pool: string
  /**
   * Running time in minutes
   */
  runningTime: number
  expiry?: number
}

export async function monitorAgent(agentId: string, ao: AoInstance): Promise<string> {
  try {
    const monitorId = await monitor({
      process: agentId,
      signer: createDataItemSigner(window.arweaveWallet),
    })

    return monitorId
  } catch (err: any) {
    if ("message" in err && err.message.includes("Process already being monitored"))
      return "unknown"
    console.error(err)
    throw new Error("Unable to monitor Agent with cron")
  }
}

export async function dcaOrder(
  data: DcaOrderParams,
  ao: AoInstance,
  onPrepared?: () => void,
): Promise<string> {
  const requestId = await ao.message({
    process: AGENTS_FACTORY,
    signer: createDataItemSigner(window.arweaveWallet),
    tags: [
      { name: "Action", value: "Create-DCA-Order" },
      { name: "Run-Time", value: String(data.runningTime * 60 * 1000) },
      { name: "Cron-Interval", value: "30-seconds" },
      //
      { name: "Amm", value: data.pool },
      { name: "Token-From", value: data.fromTokenId },
      { name: "Token-To", value: data.toTokenId },
      { name: "Input", value: data.input.toString() },
      // 8 days
      { name: "Expires-At", value: String(Date.now() + 1000 * 60 * (8 * 24 * 60)) },
    ],
  })
  console.log("onRequestId", requestId)

  const computedResult = await ao.result({
    message: requestId,
    process: AGENTS_FACTORY,
  })

  console.log("onRequestComputed", computedResult)

  if (
    computedResult.Messages.length === 0 &&
    computedResult.Spawns.length === 0 &&
    computedResult.Output.data
  ) {
    throw new Error(computedResult.Output.data)
  }

  if (
    computedResult.Messages.length === 0 &&
    computedResult.Spawns.length === 0 &&
    computedResult.Error
  ) {
    throw new Error(computedResult.Error)
  }

  const agentId = await Promise.race([
    retryable(readAgentId, LONG_OPERATION)(requestId),
    retryable(readAgentIdFromCU, LONG_OPERATION)(requestId, ao),
  ])
  console.log("onAgentId", agentId)

  const status = await retryable(readAgentStatus)(agentId, ao)
  console.log("onAgentStatus", status)

  if (status !== "AwaitingDeposit" && status !== "AwaitingStrategyConfiguration")
    throw new Error("Unexpected Agent status")

  const monitorId = await retryable(monitorAgent)(agentId, ao)
  console.log("onMonitorId", monitorId)

  onPrepared?.()

  // Ensure that we are ready to deposit
  await retryable(waitForAgentAwaitingDeposit)(agentId, ao)

  const transferId = await transfer(
    {
      token: data.fromTokenId,
      recipient: agentId,
      quantity: data.input,
    },
    createDataItemSigner(window.arweaveWallet),
    ao,
  )
  console.log("onTransferId", transferId)

  return agentId
}

export async function getDcaParams(runningTime: number, ao: AoInstance) {
  const res = await ao.dryrun({
    process: AGENTS_FACTORY,
    Timestamp: 0,
    tags: [
      { name: "Action", value: "Get-DCA-Params" },
      { name: "Run-Time", value: String(runningTime * 60 * 1000) },
    ],
  })

  const tags = flattenTags(res.Messages[0].Tags)

  return {
    tickInterval: Number(tags["Tick-Interval"]),
    runTime: tags["Run-Time"],
    endTime: tags["End-Time"],
    totalTicks: tags["Total-Ticks"],
  }
}

export interface StopLossOrderParams extends StopLosOrderFormValues {
  input: bigint
  output: bigint
  fromTokenId: string
  toTokenId: string
  pool: string
  reserves: Reserves
  fromTokenDenomination: number
  toTokenDenomination: number
}

export async function stopLossOrder(
  data: StopLossOrderParams,
  ao: AoInstance,
  onPrepared?: () => void,
): Promise<string> {
  const {
    fromTokenId,
    toTokenId,
    reserves,
    input,
    pool,
    stopLossPrice,
    variant,
    expiry,
    output,
    callback = "0",
    fromTokenDenomination,
    toTokenDenomination,
  } = data

  const tags = [
    { name: "Action", value: "Create-Stop-Order" },
    //
    { name: "Reserves-Token-From", value: String(reserves[fromTokenId]) },
    { name: "Reserves-Token-To", value: String(reserves[toTokenId]) },
    { name: "Denomination-Token-From", value: String(fromTokenDenomination) },
    { name: "Denomination-Token-To", value: String(toTokenDenomination) },
    //
    { name: "Input", value: input.toString() },
    { name: "Expires-At", value: String(Date.now() + 1000 * 60 * (expiry || 3 * 24 * 60)) },
    //
    { name: "Amm", value: pool },
    { name: "Token-From", value: fromTokenId },
    { name: "Token-To", value: toTokenId },
  ]

  if (variant === "market") {
    tags.push({
      name: "Stop-Price",
      value: parseNumberAsBigInt(String(stopLossPrice), 18).toString(),
    })
  }

  if (variant === "limit") {
    tags.push({
      name: "Stop-Price",
      value: parseNumberAsBigInt(String(stopLossPrice), 18).toString(),
    })
    tags.push({ name: "Stop-Limit-Type", value: "absolute" })
    tags.push({ name: "Is-Stop-Limit", value: String(true) })
    tags.push({ name: "Stop-Limit-Min-Output", value: output.toString() })
  }

  if (variant === "trailing") {
    tags.push({ name: "Is-Trailing", value: String(true) })
    tags.push({ name: "Trailing-Difference", value: String(parseFloat(callback) * 100) })
  }
  console.log("onStopLossTags", tags)

  const requestId = await ao.message({
    process: AGENTS_FACTORY,
    signer: createDataItemSigner(window.arweaveWallet),
    tags,
  })
  console.log("onRequestId", requestId)

  const computedResult = await ao.result({
    message: requestId,
    process: AGENTS_FACTORY,
  })

  console.log("onRequestComputed", computedResult)

  if (
    computedResult.Messages.length === 0 &&
    computedResult.Spawns.length === 0 &&
    computedResult.Output.data
  ) {
    throw new Error(computedResult.Output.data)
  }

  if (
    computedResult.Messages.length === 0 &&
    computedResult.Spawns.length === 0 &&
    computedResult.Error
  ) {
    throw new Error(computedResult.Error)
  }

  const agentId = await Promise.race([
    retryable(readAgentId, LONG_OPERATION)(requestId),
    retryable(readAgentIdFromCU, LONG_OPERATION)(requestId, ao),
  ])
  console.log("onAgentId", agentId)

  const status = await retryable(readAgentStatus)(agentId, ao)
  console.log("onAgentStatus", status)

  if (status !== "AwaitingDeposit" && status !== "AwaitingStrategyConfiguration")
    throw new Error("Unexpected Agent status")

  onPrepared?.()

  // Ensure that we are ready to deposit
  await retryable(waitForAgentAwaitingDeposit)(agentId, ao)

  const transferId = await transfer(
    {
      token: data.fromTokenId,
      recipient: agentId,
      quantity: data.input,
    },
    createDataItemSigner(window.arweaveWallet),
    ao,
  )
  console.log("onTransferId", transferId)

  return agentId
}
